<template>
  <table class="weekday-percentage-input">
    <thead>
      <tr>
        <th scope="col" />
        <th v-for="weekday in weekdays" :key="`th_${weekday}`" scope="col">
          <button
            type="button"
            class="cursor-pointer"
            @click.prevent="toggleEntireWeekday(weekday)"
          >
            <span class="sr-only">
              {{
                $texts(
                  'weekdaysPercentageInput.selectAllWeekday',
                  'Alle Tagesabschnitte auswählen am',
                )
              }}
            </span>
            {{ weekdayLabels[weekday] }}
          </button>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="dayPart in dayParts" :key="`tr_${dayPart}`">
        <th scope="row">
          <button
            type="button"
            class="cursor-pointer"
            @click.prevent="toggleEntireDayPart(dayPart)"
          >
            <span class="sr-only">
              {{
                $texts(
                  'weekdaysPercentageInput.selectAllDaypart',
                  'Alle Tage auswählen für',
                )
              }}
            </span>
            {{ dayPartLabels[dayPart] }}
          </button>
        </th>
        <td v-for="weekday in weekdays" :key="`td_${dayPart}_${weekday}`">
          <label :for="`${context?.id}_${weekday}_${dayPart}_input`">
            <span class="sr-only">
              {{
                $texts('weekdaysPercentageInput.careTakingAt', 'Betreuung am')
              }}
              {{ weekdayLabels[weekday] }}, {{ dayPartLabels[dayPart] }}
            </span>
            <input
              :id="`${context?.id}_${weekday}_${dayPart}_input`"
              v-model="checkedDayPartsPerDay[weekday][dayPart]"
              :name="`${context?.id}_${weekday}_${dayPart}_input`"
              type="checkbox"
            />
          </label>
        </td>
      </tr>
    </tbody>
  </table>

  <strong>({{ calculatedPercentage }}%)</strong>
</template>

<script lang="ts" setup>
import type { FormKitFrameworkContext } from '@formkit/core'

const props = defineProps<{
  name?: string
  required?: boolean
  modelValue?: number
  context?: FormKitFrameworkContext
}>()

const { $texts } = useNuxtApp()

// To ensure the correct order
const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']

type Weekday = (typeof weekdays)[number]

const weekdayLabels: Record<Weekday, string> = {
  monday: $texts('weekdaysPercentageInput.monday', 'Montag'),
  tuesday: $texts('weekdaysPercentageInput.tuesday', 'Dienstag'),
  wednesday: $texts('weekdaysPercentageInput.wednesday', 'Mittwoch'),
  thursday: $texts('weekdaysPercentageInput.thursday', 'Donnerstag'),
  friday: $texts('weekdaysPercentageInput.friday', 'Freitag'),
}

const dayParts = ['beforeSchool', 'morning', 'overLunch', 'afternoon']

type DayPart = (typeof dayParts)[number]

const dayPartLabels: Record<DayPart, string> = {
  beforeSchool: $texts(
    'weekdaysPercentageInput.beforeSchool',
    'Vor der Schule',
  ),
  morning: $texts('weekdaysPercentageInput.morning', 'Vormittags'),
  overLunch: $texts('weekdaysPercentageInput.overLunch', 'Über Mittag'),
  afternoon: $texts('weekdaysPercentageInput.afternoon', 'Nachmittag'),
}

const checkedDayPartsPerDay = ref<Record<Weekday, Record<DayPart, boolean>>>({
  monday: {
    beforeSchool: false,
    morning: false,
    overLunch: false,
    afternoon: false,
  },
  tuesday: {
    beforeSchool: false,
    morning: false,
    overLunch: false,
    afternoon: false,
  },
  wednesday: {
    beforeSchool: false,
    morning: false,
    overLunch: false,
    afternoon: false,
  },
  thursday: {
    beforeSchool: false,
    morning: false,
    overLunch: false,
    afternoon: false,
  },
  friday: {
    beforeSchool: false,
    morning: false,
    overLunch: false,
    afternoon: false,
  },
})

function getPercentageForWeekday(
  weekdayInput: Record<DayPart, boolean>,
): number {
  // Trivial case, we consider this to be the full day
  if (weekdayInput.morning && weekdayInput.afternoon) {
    return 20
  }

  let percentage = 0

  if (weekdayInput.beforeSchool) {
    if (weekdayInput.morning) {
      percentage += 10
    } else {
      percentage += 2
    }
  } else if (weekdayInput.morning) {
    percentage += 10
  }

  if (weekdayInput.overLunch) {
    percentage += 4
  }

  if (weekdayInput.afternoon) {
    percentage += 10
  }

  return percentage
}

function getTendency(values: boolean[]): boolean {
  return values.filter(Boolean).length > values.length / 2
}

function toggleEntireDayPart(dayPart: DayPart) {
  const currentTendency = getTendency(
    Object.values(checkedDayPartsPerDay.value).map((day) => day[dayPart]),
  )

  checkedDayPartsPerDay.value.monday[dayPart] = !currentTendency
  checkedDayPartsPerDay.value.tuesday[dayPart] = !currentTendency
  checkedDayPartsPerDay.value.wednesday[dayPart] = !currentTendency
  checkedDayPartsPerDay.value.thursday[dayPart] = !currentTendency
  checkedDayPartsPerDay.value.friday[dayPart] = !currentTendency
}

function toggleEntireWeekday(weekday: Weekday) {
  const currentTendency = getTendency(
    Object.values(checkedDayPartsPerDay.value[weekday]),
  )

  checkedDayPartsPerDay.value[weekday].beforeSchool = !currentTendency
  checkedDayPartsPerDay.value[weekday].morning = !currentTendency
  checkedDayPartsPerDay.value[weekday].overLunch = !currentTendency
  checkedDayPartsPerDay.value[weekday].afternoon = !currentTendency
}

const calculatedPercentage = computed(() => {
  return weekdays
    .map((day) => getPercentageForWeekday(checkedDayPartsPerDay.value[day]))
    .reduce((a: number, b: number) => a + b, 0)
})

watch(calculatedPercentage, (newValue: number) => {
  if (!props.context?.node) {
    return
  }
  props.context.node.input(newValue)
})
</script>

<style lang="postcss">
@import '~/assets/css/components/form-elements/weekday-percentage-input.css';
</style>
